import { useEffect, useState, useRef } from 'react';
import { Save, Send, Trash2, Upload, Plus, X, Copy, Check, FileText, Image as ImageIcon, Film } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';

const CONCLUSIONS = [
  { k: 'recommended', label: 'RECOMMANDÉ', color: 'bg-green-500 text-white' },
  { k: 'recommended_with_conditions', label: 'RECOMMANDÉ SOUS CONDITIONS', color: 'bg-amber-500 text-white' },
  { k: 'more_verification', label: 'VÉRIFICATIONS SUPPLÉMENTAIRES REQUISES', color: 'bg-blue-500 text-white' },
  { k: 'not_recommended', label: 'NON RECOMMANDÉ', color: 'bg-red-500 text-white' },
];

const COMPLIANCE_STATUSES = [
  { k: 'conforme', label: 'CONFORME', color: 'bg-green-100 text-green-800 border-green-400' },
  { k: 'non_conforme', label: 'NON CONFORME', color: 'bg-red-100 text-red-800 border-red-400' },
  { k: 'a_corriger', label: 'À CORRIGER', color: 'bg-amber-100 text-amber-800 border-amber-400' },
  { k: 'a_verifier', label: 'À VÉRIFIER', color: 'bg-blue-100 text-blue-800 border-blue-400' },
  { k: 'test_independant_requis', label: 'TEST INDÉPENDANT REQUIS', color: 'bg-purple-100 text-purple-800 border-purple-400' },
];

const INFO_STATUSES = [
  { k: '', label: '—' },
  { k: 'declared', label: 'DÉCLARÉ' },
  { k: 'document_received', label: 'DOC. REÇU' },
  { k: 'yuville_verified', label: 'VÉRIFIÉ YUVILLE' },
  { k: 'independent_verification_required', label: 'VÉRIF. INDÉP. REQUISE' },
  { k: 'not_verified', label: 'NON VÉRIFIÉ' },
  { k: 'anomaly_to_resolve', label: 'ANOMALIE À RÉSOUDRE' },
];

const ISSUE_STATUSES = [
  { k: 'open', label: 'OUVERT' },
  { k: 'in_progress', label: 'EN COURS' },
  { k: 'resolved', label: 'RÉSOLU' },
  { k: 'accepted_by_client', label: 'ACCEPTÉ PAR CLIENT' },
];

const FIELD_A = [
  ['factoryName', 'Nom de l\'usine'], ['address', 'Adresse'], ['visitDate', 'Date de visite'],
  ['auditor', 'Auditeur / représentant'], ['peopleMet', 'Personnes rencontrées'],
  ['installations', 'Installations observées'], ['productionLines', 'Lignes de production'],
  ['equipments', 'Principaux équipements'], ['headcount', 'Effectifs'],
  ['declaredCapacity', 'Capacité déclarée'], ['observedCapacity', 'Capacité constatée'],
  ['organization', 'Organisation de la production'], ['qualityProcedures', 'Contrôle qualité'],
  ['storage', 'Entreposage'], ['packaging', 'Conditionnement'],
  ['traceability', 'Traçabilité'], ['similarExperience', 'Expérience comparable'],
  ['capacityFit', 'Capacité à respecter volumes/délais'],
];

const FIELD_C = [
  ['chineseName', 'Raison sociale (chinois)'], ['englishName', 'Raison sociale (anglais)'],
  ['uscc', 'Unified Social Credit Code'], ['businessLicense', 'Licence commerciale'],
  ['foundationDate', 'Date de constitution'], ['registeredAddress', 'Adresse enregistrée'],
  ['legalRep', 'Représentant légal'], ['businessScope', 'Activités enregistrées'],
  ['entityStatus', 'Statut de l\'entreprise'],
  ['directExportCapacity', 'Capacité à exporter directement'],
  ['exporter', 'Exportateur utilisé (si indirect)'],
  ['licenses', 'Licences / autorisations'], ['certifications', 'Certifications annoncées'],
  ['certifyingBodies', 'Organismes ayant délivré'],
  ['ipElements', 'Propriété intellectuelle'], ['legalIssues', 'Litiges / anomalies'],
  ['otherCompliance', 'Autres éléments'],
];

const VERIFIABLE_FIELDS = new Set(['declaredCapacity', 'observedCapacity', 'qualityProcedures', 'traceability', 'similarExperience', 'uscc', 'businessLicense', 'legalRep', 'certifications', 'directExportCapacity']);

const inp = 'w-full px-3 py-2 rounded border border-gray-300 focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 outline-none text-sm bg-white';
const ta = inp + ' min-h-[60px] resize-y';

export default function AuditManager({ manufacturerId, isValidatedShortlist }) {
  const { toast } = useToast();
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get(`/admin/manufacturers/${manufacturerId}/audit`);
      setA(res.data);
    } catch (e) { toast({ title: 'Erreur chargement audit' }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [manufacturerId]);

  if (!a) return <div className="text-xs text-gray-400">Chargement de l'audit…</div>;

  const set = (k, v) => setA({ ...a, [k]: v });
  const setInfoStatus = (field, val) => setA({ ...a, infoStatus: { ...(a.infoStatus || {}), [field]: val } });

  const save = async () => {
    setBusy(true);
    try {
      // Strip attachments (they're managed separately)
      const payload = { ...a };
      delete payload.attachments; delete payload.id; delete payload.manufacturerId; delete payload.rfqId;
      delete payload.publishedAt; delete payload.createdAt; delete payload.updatedAt; delete payload.history;
      delete payload.clientDecision; delete payload.clientDecisionAt;
      const res = await api.put(`/admin/manufacturers/${manufacturerId}/audit`, payload);
      setA(res.data);
      toast({ title: 'Audit enregistré' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({ title: 'Erreur', description: typeof detail === 'string' ? detail : 'Enregistrement impossible', duration: 12000 });
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/manufacturers/${manufacturerId}/audit/publish`);
      setPublishedUrl(res.data.url);
      toast({ title: 'Rapport publié', description: res.data.emailSent ? 'E-mail envoyé au client' : 'Lien généré (e-mail non envoyé)', duration: 12000 });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Publication impossible', duration: 12000 });
    } finally { setBusy(false); }
  };

  const copyUrl = () => { navigator.clipboard.writeText(publishedUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const addGridRow = () => setA({ ...a, complianceGrid: [...(a.complianceGrid || []), { requirement: '', verificationMethod: '', finding: '', status: '', comment: '' }] });
  const updateGrid = (i, k, v) => { const g = [...a.complianceGrid]; g[i] = { ...g[i], [k]: v }; setA({ ...a, complianceGrid: g }); };
  const removeGrid = (i) => setA({ ...a, complianceGrid: a.complianceGrid.filter((_, idx) => idx !== i) });

  const addIssue = () => setA({ ...a, openIssues: [...(a.openIssues || []), { label: '', source: '', status: 'open', notes: '' }] });
  const updateIssue = (i, k, v) => { const g = [...a.openIssues]; g[i] = { ...g[i], [k]: v }; setA({ ...a, openIssues: g }); };
  const removeIssue = (i) => setA({ ...a, openIssues: a.openIssues.filter((_, idx) => idx !== i) });

  const uploadFiles = async (files, kind) => {
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', f); fd.append('kind', kind); fd.append('linked_to', '');
      try {
        await api.post(`/admin/manufacturers/${manufacturerId}/audit/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (e) {
        toast({ title: 'Upload échoué', description: e?.response?.data?.detail || f.name, duration: 8000 });
      }
    }
    load();
  };

  const deleteAttachment = async (i) => {
    if (!window.confirm('Supprimer cette pièce ?')) return;
    await api.delete(`/admin/manufacturers/${manufacturerId}/audit/attachments/${i}`);
    load();
  };

  const clientDecisionLabels = { accept: '✅ Accepté', request_corrections: '🔧 Correctifs', request_more_verification: '🔍 Vérif. supp.', reject: '❌ Rejeté' };

  return (
    <div className="mt-4 border-t-2 border-brand-yellow pt-4">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h4 className="font-bold text-brand-navy text-sm">🔍 Audit sur place</h4>
        <div className="flex gap-2 flex-wrap">
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400 disabled:opacity-60">
            <Save className="w-3.5 h-3.5" /> Enregistrer
          </button>
          <button onClick={publish} disabled={busy || !a.conclusion} title={!a.conclusion ? 'Choisissez une conclusion avant de publier' : ''} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-navy text-white text-xs font-bold hover:bg-brand-navy/90 disabled:opacity-60">
            <Send className="w-3.5 h-3.5" /> Publier & notifier client
          </button>
          {a.clientDecision && (
            <span className="text-xs px-3 py-1.5 rounded-full bg-white border border-gray-300 text-brand-navy font-semibold">
              Décision client : {clientDecisionLabels[a.clientDecision] || a.clientDecision}
            </span>
          )}
        </div>
      </div>

      {publishedUrl && (
        <div className="bg-brand-yellow/10 border border-brand-yellow rounded p-3 mb-4 text-xs">
          <div className="font-bold text-brand-navy mb-1">Lien rapport client :</div>
          <div className="flex items-center gap-2">
            <input readOnly value={publishedUrl} className="flex-1 min-w-0 px-2 py-1 bg-white border border-gray-200 rounded text-[11px] font-mono" onFocus={(e) => e.target.select()} />
            <button onClick={copyUrl} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-gray-200 hover:border-brand-navy">
              {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />} {copied ? 'Copié' : 'Copier'}
            </button>
          </div>
        </div>
      )}

      {/* Volet A */}
      <details open className="mb-3 bg-gray-50 rounded border border-gray-200">
        <summary className="cursor-pointer p-3 font-bold text-brand-navy text-sm">A · Usine & capacités de production</summary>
        <div className="p-3 grid sm:grid-cols-2 gap-3">
          {FIELD_A.map(([k, label]) => (
            <div key={k} className={['installations', 'productionLines', 'organization', 'qualityProcedures'].includes(k) ? 'sm:col-span-2' : ''}>
              <label className="block text-xs font-semibold text-brand-navy mb-1">
                {label}
                {VERIFIABLE_FIELDS.has(k) && (
                  <select value={a.infoStatus?.[k] || ''} onChange={(e) => setInfoStatus(k, e.target.value)} className="ml-2 text-[10px] px-1 py-0.5 border border-gray-300 rounded">
                    {INFO_STATUSES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                  </select>
                )}
              </label>
              {['installations', 'productionLines', 'organization', 'qualityProcedures'].includes(k) ? (
                <textarea value={a[k] || ''} onChange={(e) => set(k, e.target.value)} className={ta} />
              ) : (
                <input type="text" value={a[k] || ''} onChange={(e) => set(k, e.target.value)} className={inp} />
              )}
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-brand-navy mb-1">Observations générales de l'auditeur</label>
            <textarea value={a.factoryNotes || ''} onChange={(e) => set('factoryNotes', e.target.value)} className={ta} />
          </div>
        </div>
      </details>

      {/* Volet B */}
      <details open className="mb-3 bg-gray-50 rounded border border-gray-200">
        <summary className="cursor-pointer p-3 font-bold text-brand-navy text-sm">B · Produit & qualité (grille de conformité)</summary>
        <div className="p-3">
          {(a.complianceGrid || []).length === 0 && <p className="text-xs text-gray-400 italic mb-2">Aucune exigence ajoutée. Ajoutez celles du cahier des charges.</p>}
          {(a.complianceGrid || []).map((row, i) => (
            <div key={i} className="mb-3 p-3 bg-white rounded border border-gray-200">
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-[10px] uppercase tracking-widest font-bold text-brand-yellow">Exigence {i + 1}</span>
                <button onClick={() => removeGrid(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <input placeholder="Exigence (ex. Tension 25 kV)" value={row.requirement || ''} onChange={(e) => updateGrid(i, 'requirement', e.target.value)} className={inp} />
                <input placeholder="Méthode de vérification" value={row.verificationMethod || ''} onChange={(e) => updateGrid(i, 'verificationMethod', e.target.value)} className={inp} />
                <textarea placeholder="Constat" value={row.finding || ''} onChange={(e) => updateGrid(i, 'finding', e.target.value)} className={ta + ' sm:col-span-2'} />
                <select value={row.status || ''} onChange={(e) => updateGrid(i, 'status', e.target.value)} className={inp}>
                  <option value="">— Statut —</option>
                  {COMPLIANCE_STATUSES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                </select>
                <input placeholder="Commentaire / preuve" value={row.comment || ''} onChange={(e) => updateGrid(i, 'comment', e.target.value)} className={inp} />
              </div>
            </div>
          ))}
          <button onClick={addGridRow} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-brand-yellow text-brand-navy text-xs font-semibold hover:bg-brand-yellow/10">
            <Plus className="w-3.5 h-3.5" /> Ajouter une exigence
          </button>
        </div>
      </details>

      {/* Volet C */}
      <details open className="mb-3 bg-gray-50 rounded border border-gray-200">
        <summary className="cursor-pointer p-3 font-bold text-brand-navy text-sm">C · Juridique & commercial</summary>
        <div className="p-3 grid sm:grid-cols-2 gap-3">
          {FIELD_C.map(([k, label]) => (
            <div key={k} className={['businessScope', 'legalIssues', 'otherCompliance'].includes(k) ? 'sm:col-span-2' : ''}>
              <label className="block text-xs font-semibold text-brand-navy mb-1">
                {label}
                {VERIFIABLE_FIELDS.has(k) && (
                  <select value={a.infoStatus?.[k] || ''} onChange={(e) => setInfoStatus(k, e.target.value)} className="ml-2 text-[10px] px-1 py-0.5 border border-gray-300 rounded">
                    {INFO_STATUSES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                  </select>
                )}
              </label>
              {['businessScope', 'legalIssues', 'otherCompliance', 'ipElements'].includes(k) ? (
                <textarea value={a[k] || ''} onChange={(e) => set(k, e.target.value)} className={ta} />
              ) : (
                <input type="text" value={a[k] || ''} onChange={(e) => set(k, e.target.value)} className={inp} />
              )}
            </div>
          ))}
        </div>

        <div className="p-3 border-t border-gray-200 bg-brand-yellow/5">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-yellow mb-2">⚠ Cohérence des entités commerciales</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <input placeholder="Usine / manufacturier" value={a.factoryOperator || ''} onChange={(e) => set('factoryOperator', e.target.value)} className={inp} />
            <input placeholder="Cocontractant" value={a.contractingEntity || ''} onChange={(e) => set('contractingEntity', e.target.value)} className={inp} />
            <input placeholder="Émetteur de la facture" value={a.invoicingEntity || ''} onChange={(e) => set('invoicingEntity', e.target.value)} className={inp} />
            <input placeholder="Bénéficiaire du paiement" value={a.paymentBeneficiary || ''} onChange={(e) => set('paymentBeneficiary', e.target.value)} className={inp} />
          </div>
          <textarea placeholder="Explication de la relation entre les entités (si différentes)" value={a.entityCoherence || ''} onChange={(e) => set('entityCoherence', e.target.value)} className={ta + ' mt-2'} />
        </div>
      </details>

      {/* Attachments */}
      <details className="mb-3 bg-gray-50 rounded border border-gray-200">
        <summary className="cursor-pointer p-3 font-bold text-brand-navy text-sm">Pièces jointes ({(a.attachments || []).length})</summary>
        <div className="p-3">
          <div className="flex gap-2 mb-3 flex-wrap">
            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { const kind = fileRef.current.dataset.kind || 'document'; uploadFiles(e.target.files, kind); e.target.value = ''; }} />
            <button onClick={() => { fileRef.current.dataset.kind = 'photo'; fileRef.current.accept = 'image/*'; fileRef.current.click(); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 text-brand-navy text-xs hover:border-brand-yellow">
              <ImageIcon className="w-3.5 h-3.5" /> Photos
            </button>
            <button onClick={() => { fileRef.current.dataset.kind = 'video'; fileRef.current.accept = 'video/*'; fileRef.current.click(); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 text-brand-navy text-xs hover:border-brand-yellow">
              <Film className="w-3.5 h-3.5" /> Vidéos
            </button>
            <button onClick={() => { fileRef.current.dataset.kind = 'document'; fileRef.current.accept = ''; fileRef.current.click(); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 text-brand-navy text-xs hover:border-brand-yellow">
              <FileText className="w-3.5 h-3.5" /> Documents
            </button>
          </div>
          {(a.attachments || []).length > 0 && (
            <ul className="space-y-1">
              {a.attachments.map((at, i) => (
                <li key={i} className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded text-xs">
                  <span className="truncate flex items-center gap-2">
                    {at.kind === 'photo' && <ImageIcon className="w-3.5 h-3.5 text-brand-yellow" />}
                    {at.kind === 'video' && <Film className="w-3.5 h-3.5 text-brand-yellow" />}
                    {at.kind === 'document' && <FileText className="w-3.5 h-3.5 text-brand-yellow" />}
                    {at.filename} <span className="text-gray-400">({Math.round(at.size / 1024)} Ko)</span>
                  </span>
                  <button onClick={() => deleteAttachment(i)} className="text-red-500 hover:text-red-700"><X className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>

      {/* Open issues */}
      <details className="mb-3 bg-gray-50 rounded border border-gray-200">
        <summary className="cursor-pointer p-3 font-bold text-brand-navy text-sm">Points à résoudre avant contrat ({(a.openIssues || []).length})</summary>
        <div className="p-3">
          {(a.openIssues || []).map((it, i) => (
            <div key={i} className="mb-2 p-2 bg-white rounded border border-gray-200 grid sm:grid-cols-4 gap-2 items-center">
              <input placeholder="Description" value={it.label || ''} onChange={(e) => updateIssue(i, 'label', e.target.value)} className={inp + ' sm:col-span-2'} />
              <select value={it.status || 'open'} onChange={(e) => updateIssue(i, 'status', e.target.value)} className={inp}>
                {ISSUE_STATUSES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
              </select>
              <button onClick={() => removeIssue(i)} className="text-red-500 hover:text-red-700 justify-self-end"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={addIssue} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-brand-yellow text-brand-navy text-xs font-semibold hover:bg-brand-yellow/10">
            <Plus className="w-3.5 h-3.5" /> Ajouter un point
          </button>
        </div>
      </details>

      {/* Conclusion */}
      <div className="mb-3 p-4 bg-brand-navy text-white rounded">
        <div className="text-xs font-bold uppercase tracking-widest text-brand-yellow mb-3">Conclusion de Yuville</div>
        <div className="grid sm:grid-cols-2 gap-2 mb-3">
          {CONCLUSIONS.map((c) => (
            <label key={c.k} className={`cursor-pointer p-3 rounded border-2 text-xs font-bold ${a.conclusion === c.k ? c.color + ' border-white' : 'bg-white/10 border-white/30 text-white/80 hover:border-white'}`}>
              <input type="radio" name="conclusion" checked={a.conclusion === c.k} onChange={() => set('conclusion', c.k)} className="sr-only" />
              {c.label}
            </label>
          ))}
        </div>
        <textarea value={a.conclusionExplanation || ''} onChange={(e) => set('conclusionExplanation', e.target.value)} placeholder="Explication détaillée de la conclusion (visible par le client)" className="w-full px-3 py-2 rounded text-brand-navy text-sm min-h-[80px]" />
      </div>

      {/* Internal notes */}
      <div>
        <label className="block text-xs font-bold text-red-700 mb-1">🔒 Notes internes (jamais visibles par le client)</label>
        <textarea value={a.internalNotes || ''} onChange={(e) => set('internalNotes', e.target.value)} className={ta + ' bg-red-50'} />
      </div>
    </div>
  );
}
